import React, { useCallback, useMemo, useRef } from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import type { Chart as ChartJS } from 'chart.js';
import { Line } from 'react-chartjs-2';
import KpiChartPanel from './KpiChartPanel';
import { formatAxisCompactK, formatDataLabelCompact } from '../chartFormatters';
import { KPI_LINE_ACCENT } from './kpiChartPalette';

function rollingMonthLabels(): string[] {
  const anchor = new Date();
  const out: string[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() - (11 - i), 1);
    out.push(d.toLocaleString('en-US', { month: 'short', year: 'numeric' }));
  }
  return out;
}

export interface KpiChangesMonthlyLineChartProps {
  monthlyTrendChange: number[];
  /** Same length as `monthlyTrendChange` when provided (KPI charts API). */
  monthLabels?: string[];
  onDrillMonth?: (monthIndex: number) => void;
}

const KpiChangesMonthlyLineChart: React.FC<KpiChangesMonthlyLineChartProps> = ({
  monthlyTrendChange,
  monthLabels,
  onDrillMonth,
}) => {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const n = monthlyTrendChange.length;
  const labels = useMemo(() => {
    if (monthLabels && monthLabels.length === n) return monthLabels;
    if (n === 12) return rollingMonthLabels();
    return Array.from({ length: n }, (_, i) => String(i + 1));
  }, [monthLabels, n]);
  const data = monthlyTrendChange;
  const maxVol = Math.max(0, ...data, 1);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const chart = chartRef.current;
      if (!chart || !onDrillMonth) return;
      const els = chartElementAtEvent(chart, event);
      const el = els[0];
      if (el == null) return;
      const idx = el.index;
      if (data[idx] <= 0) return;
      onDrillMonth(idx);
    },
    [onDrillMonth, data]
  );

  return (
    <KpiChartPanel title="Changes by Date" className="h-full w-full" flexFill titleClassName="text-center">
      <div className="relative min-h-[280px] w-full flex-1 px-1">
        <Line
          ref={chartRef}
          onClick={onDrillMonth ? handleClick : undefined}
          data={{
            labels,
            datasets: [
              {
                label: 'Changes',
                data,
                borderColor: KPI_LINE_ACCENT,
                backgroundColor: 'rgba(52, 101, 164, 0.12)',
                fill: true,
                tension: 0.25,
                pointRadius: 3,
                pointHoverRadius: 6,
                pointBackgroundColor: '#fff',
                pointBorderColor: KPI_LINE_ACCENT,
                pointBorderWidth: 2,
                borderWidth: 2,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 2,
                  clip: false,
                  color: KPI_LINE_ACCENT,
                  font: { weight: 600, size: 9 },
                  formatter: (value: number) => formatDataLabelCompact(Number(value)),
                },
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 18, right: 8, bottom: 4 } },
            interaction: { mode: 'nearest', intersect: true },
            onHover: (event, elements, chart) => {
              const canvas = chart.canvas;
              if (!elements.length) {
                canvas.style.cursor = 'default';
                return;
              }
              const pt = data[elements[0].index];
              canvas.style.cursor = onDrillMonth && pt > 0 ? 'pointer' : 'default';
            },
            plugins: {
              legend: { display: false },
            },
            scales: {
              x: {
                grid: { display: false, drawTicks: true },
                ticks: {
                  color: '#64748b',
                  maxRotation: 45,
                  minRotation: 0,
                  autoSkip: true,
                  maxTicksLimit: 12,
                  font: { size: 9 },
                },
              },
              y: {
                beginAtZero: true,
                suggestedMax: maxVol * 1.15,
                grid: { color: 'rgba(148, 163, 184, 0.35)' },
                border: { display: false },
                ticks: {
                  color: '#94a3b8',
                  callback: (v) => formatAxisCompactK(v as number),
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default KpiChangesMonthlyLineChart;
